import { useCallback, useEffect, useRef } from 'react'
import type { MouseEvent as ReactMouseEvent } from 'react'

/** The label root rendered by Canvas.tsx — clicks within it are "on the label". */
const SURFACE_SELECTOR = '[data-testid="canvas-surface"]'
/** Controls that own their own click semantics (e.g. ruler dimension inputs). */
const INTERACTIVE_SELECTOR = 'input,button,select,textarea,[role="button"],a'

export interface UseCanvasDeselectOptions {
  /** Clear the current region selection. */
  onDeselect: () => void
  /**
   * What Escape does. Defaults to `onDeselect`. The template designer overrides
   * this to cancel a picked-but-not-dropped block first, then deselect.
   */
  onEscape?: () => void
  /** When false (e.g. a modal is open), Escape is ignored. Default true. */
  escapeEnabled?: boolean
}

export interface UseCanvasDeselectResult {
  /** Spread onto the canvas-centering pane element. */
  onPaneClick: (e: ReactMouseEvent) => void
}

function isEditableTarget(el: Element | null): boolean {
  if (!el) return false
  const tag = el.tagName
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true
  return (el as HTMLElement).isContentEditable === true
}

export function useCanvasDeselect({
  onDeselect,
  onEscape,
  escapeEnabled = true,
}: UseCanvasDeselectOptions): UseCanvasDeselectResult {
  // Hold the latest callbacks/flags in a ref so the window listener installs
  // once and never re-subscribes per render (mirrors state/useHistory.ts intent).
  const latest = useRef({ onDeselect, onEscape, escapeEnabled })
  latest.current = { onDeselect, onEscape, escapeEnabled }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const { onDeselect, onEscape, escapeEnabled } = latest.current
      if (!escapeEnabled) return
      if (isEditableTarget(document.activeElement)) return
      ;(onEscape ?? onDeselect)()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  const onPaneClick = useCallback((e: ReactMouseEvent) => {
    const target = e.target as Element | null
    if (!target) return
    if (target.closest(SURFACE_SELECTOR)) return // click landed on the label
    if (target.closest(INTERACTIVE_SELECTOR)) return // ruler input, button, etc.
    latest.current.onDeselect()
  }, [])

  return { onPaneClick }
}
